<template>
  <div class="flex items-center justify-center space-x-4">
    <div
      class="flex items-center justify-center w-40 h-24 overflow-hidden border border-gray-200 rounded-md shadow-sm"
      :style="{ aspectRatio }"
    >
      <img v-if="url" class="w-full h-full" data-testid="file-url" :src="url" alt="file.name" />
      <PhotoIcon v-else class="w-8 h-8 mx-auto text-gray-200" data-testid="default-img" />
    </div>

    <div class="flex-1 text-sm">
      <template v-if="file && file.name">
        <VButton class="block" data-testid="edit-file-btn" size="xs" variant="light" @click="$emit('file-edited')">Edit Image</VButton>
        <VButton class="block mt-2" data-testid="replace-btn" size="xs" variant="light" @click="$emit('file-replaced'), removeOldFile">
          Replace
        </VButton>
      </template>

      <template v-else>
        <VButton class="block" data-testid="select-file-btn" size="xs" variant="light" @click="$emit('file-changed')">
          Select Existing File
        </VButton>
        <div>
          <VButton class="block mt-2" data-testid="upload-file-btn" size="xs" variant="light" @click="uploadNewFile">
            Upload New File
          </VButton>
          <input ref="fileInput" class="hidden" type="file" :accept="acceptedTypes" />
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { ref, computed, toRef } from 'vue'
import { PhotoIcon } from '@heroicons/vue/24/outline'
import type { Folder } from '@/entities/folder'

const props = defineProps({
  folder: {
    type: Object as PropType<Folder | null>,
    default: null,
  },
  originalFile: {
    type: File as PropType<File | null>,
    default: null,
  },
  aspectRatio: {
    type: Number,
    default: 16 / 9,
  },
})

const emit = defineEmits(['file-changed', 'file-uploaded', 'file-replaced', 'file-edited'])

const fileInput = ref<HTMLInputElement | null>(null)
const { file, removeOldFile, uploadNewFile, getFileAcceptedType } = useFileUploader({
  fileInput,
  originalFile: toRef(props, 'originalFile'),
  onFileUploaded: (file: File) => emit('file-uploaded', file),
})

const url = computed(() => (file.value ? URL.createObjectURL(file.value) : ''))
const acceptedTypes = computed(() => getFileAcceptedType(props.folder?.name?.toLowerCase()))
</script>
